import { IS_DEV_ENV } from "constants/app.constants";
import { CalculationMethod } from "constants/charts.enums";
import { EntityType } from "constants/entities.enum";
import { createEntityState } from "entities/charts/factory";
import { createMergeWithDefaults } from "entities/utils";
import { EvaChartFeatures } from "types";

import {
  DEFAULT_HIGHLIGHTED_LINE_OPACITY_FEATURE_ENTITY,
  DEFAULT_HIGHLIGHTED_LINE_SIZE_FEATURE_ENTITY,
  DEFAULT_LINE_OPACITY_FEATURE_ENTITY,
  DEFAULT_LINE_SIZE_FEATURE_ENTITY,
  DEFAULT_POINT_OPACITY_FEATURE_ENTITY,
  DEFAULT_POINT_SIZE_FEATURE_ENTITY
} from "./feature";

// Injector Total has no product switcher, no axis selectors (X/Y are fixed server-side — see
// useAxisFieldSelection.tsx, which deliberately omits EvaChart.InjectorTotal), and no
// decline-curve concepts (injector count is not a rate/decline metric), so this feature set
// follows the CrossPlot/Probit "not product-switching" precedent while disabling the decline-
// specific controls that don't apply to an Injector Count line chart (p10/p50/p90/average,
// cutoff, shutInMonths, movingAverageDays, survivorBias, useWeightedRatioAverage, lockUnits).
//
// Mirrors the Rig/Frac toolbar precedent (EVA-76): source, timeStep, normalization,
// isProducingRateType, typewells, spaghetti, and forecast are shown disabled
// (show:true, enabled:false) rather than hidden, for visual consistency with the standard
// chart toolbar even though none of them apply to a fixed-axis Injector Count chart.
const INJECTOR_TOTAL_FEATURES: Partial<EvaChartFeatures> = {
  screenshot: createEntityState(EntityType.ScreenshotFeature, {
    show: true,
    enabled: true
  }),
  fullScreen: createEntityState(EntityType.ChartsFeature, {
    show: true,
    enabled: true
  }),
  datatable: createEntityState(EntityType.ChartDataTableFeature, {
    show: true,
    enabled: true
  }),
  spaghetti: createEntityState(EntityType.ChartsFeature, {
    show: true,
    enabled: false
  }),
  forecast: createEntityState(EntityType.ChartsFeature, {
    show: true,
    enabled: false
  }),
  source: createEntityState(EntityType.SourceFeature, {
    show: true,
    enabled: false
  }),
  timeStep: createEntityState(EntityType.TimeStepFeature, {
    show: true,
    enabled: false
  }),
  normalization: createEntityState(EntityType.NormalizationFeature, {
    show: true,
    enabled: false
  }),
  isProducingRateType: createEntityState(EntityType.ChartsFeature, {
    show: true,
    enabled: false
  }),
  typewells: createEntityState(EntityType.ChartsFeature, {
    show: true,
    enabled: false
  }),
  debug: createEntityState(EntityType.ChartsFeature, {
    show: IS_DEV_ENV,
    enabled: IS_DEV_ENV
  }),
  lasso: createEntityState(EntityType.LassoFeature, {
    show: true,
    enabled: true
  }),
  chartFocus: createEntityState(EntityType.ChartFocusFeature, {
    show: true,
    enabled: true
  }),
  legend: createEntityState(EntityType.ChartsFeature, {
    show: true,
    enabled: true
  }),
  lineSize: {
    ...DEFAULT_LINE_SIZE_FEATURE_ENTITY,
    show: false,
    enabled: false
  },
  lineOpacity: {
    ...DEFAULT_LINE_OPACITY_FEATURE_ENTITY,
    show: false,
    enabled: false
  },
  highlightedLineSize: {
    ...DEFAULT_HIGHLIGHTED_LINE_SIZE_FEATURE_ENTITY,
    show: false,
    enabled: false
  },
  highlightedLineOpacity: {
    ...DEFAULT_HIGHLIGHTED_LINE_OPACITY_FEATURE_ENTITY,
    show: false,
    enabled: false
  },
  pointSize: {
    ...DEFAULT_POINT_SIZE_FEATURE_ENTITY,
    show: false,
    enabled: false
  },
  pointOpacity: {
    ...DEFAULT_POINT_OPACITY_FEATURE_ENTITY,
    show: false,
    enabled: false
  },
  // Y is always Injector Count via CalculationMethod.Count; the selector itself is not shown
  // (matches StackedBar/TrendDate, which also leave show/enabled at their false defaults here).
  calculationMethod: createEntityState(EntityType.CalculationMethodFeature, {
    properties: {
      method: CalculationMethod.Count
    }
  }),
  // Axes are fixed server-side (no field-selector UI), so chartTypeParam stays at its generic
  // empty default — same pattern as MaterialBalanceTime, another fully axis-locked chart type.
  chartTypeParam: createEntityState(EntityType.ChartTypeParamFeature, {}),
  bestFitToggle: createEntityState(EntityType.BestFitToggleFeature, {}),
  histogramToggle: createEntityState(EntityType.HistogramToggleFeature, {}),
  jitterPlotToggle: createEntityState(EntityType.JitterPlotToggleFeature, {}),
  quarterSlope: createEntityState(EntityType.SlopeLineFeature, {}),
  unitSlope: createEntityState(EntityType.SlopeLineFeature, {}),
  halfSlope: createEntityState(EntityType.SlopeLineFeature, {}),
  useProbitLineOfBestFitData: createEntityState(EntityType.ChartsFeature, {})
};

export const injectorTotalFeatures = createMergeWithDefaults(INJECTOR_TOTAL_FEATURES);
